import { useEffect, useRef } from 'react';
import { modalStackManager } from '../Utils/ModalStackManager';

// isHidden: the modal is temporarily hidden behind a child dialog.
// It should stay in the history stack — not treated as genuinely closed.
export const useModalBackButton = (open, onOpenChange, prefix = 'modal', isHidden = false) => {
  const idRef = useRef(`${prefix}-${Math.random().toString(36).substr(2, 9)}`);
  const wasOpenRef = useRef(false);
  const isHiddenRef = useRef(false);
  const closedViaBackButtonRef = useRef(false);

  // Keep onOpenChange in a ref so it's always current without being a dep.
  // This prevents the effect from re-running (and double-pushing history entries)
  // whenever the parent re-renders with a new inline callback reference.
  const onOpenChangeRef = useRef(onOpenChange);
  useEffect(() => {
    onOpenChangeRef.current = onOpenChange;
  }); // no dep array — syncs after every render

  useEffect(() => {
    modalStackManager.initialize();
  }, []);

  useEffect(() => {
    const id = idRef.current;

    if (open && !wasOpenRef.current && !isHiddenRef.current) {
      // Genuinely opening for the first time
      wasOpenRef.current = true;
      isHiddenRef.current = false;
      closedViaBackButtonRef.current = false;

      modalStackManager.push(id, () => {
        closedViaBackButtonRef.current = true;
        if (onOpenChangeRef.current) {
          onOpenChangeRef.current(false);
        }
      });
    } else if (open && wasOpenRef.current && isHiddenRef.current) {
      // Resuming from hidden state — already in the stack, just clear the flag.
      isHiddenRef.current = false;
    } else if (!open && wasOpenRef.current) {
      if (isHidden) {
        // Temporarily hidden by a child dialog — stay in the stack.
        isHiddenRef.current = true;
      } else {
        // Genuinely closed
        wasOpenRef.current = false;
        isHiddenRef.current = false;
        modalStackManager.remove(id, closedViaBackButtonRef.current);
        closedViaBackButtonRef.current = false;

        if (modalStackManager.stack.length === 0) {
          modalStackManager.scheduleCleanup();
        }
      }
    }

    return () => {
      // Safety net: component unmounts while modal is open or hidden.
      if (wasOpenRef.current) {
        const wasViaBack = closedViaBackButtonRef.current;
        wasOpenRef.current = false;
        isHiddenRef.current = false;
        closedViaBackButtonRef.current = false;
        modalStackManager.remove(id, wasViaBack);
        if (!wasViaBack && modalStackManager.stack.length === 0) {
          modalStackManager.scheduleCleanup();
        }
      }
    };
  }, [open, isHidden]); // isHidden is a stable boolean, safe to dep on
};
